import React, { useState } from 'react';
import styles from './TeacherMissionViewer.module.css';

// SimpleMarkdown component (copied from MissionForm.js)
const SimpleMarkdown = ({ children }) => {
  const text = children || '';
  
  // Convert markdown to HTML
  let html = text
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h4>$1</h4>')
    .replace(/^# (.*$)/gim, '<h5>$1</h5>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^\* (.*$)/gim, '<li>$1</li>')
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    // Line breaks
    .replace(/\n/g, '<br>');

  // Wrap lists in ul tags
  html = html.replace(/(<li>.*<\/li>)/g, '<ul>$1</ul>');

  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

const TeacherMissionViewer = ({ mission, onClose }) => {
  const [viewMode, setViewMode] = useState('details'); // 'details' or 'workspace'

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft':
        return styles.statusDraft || 'badge bg-warning';
      case 'active':
        return styles.statusActive || 'badge bg-success';
      case 'assigned':
        return styles.statusAssigned;
      case 'in-progress':
        return styles.statusInProgress;
      case 'completed':
        return styles.statusCompleted;
      case 'overdue':
        return styles.statusOverdue;
      case 'paused':
        return styles.statusPaused || 'badge bg-secondary';
      case 'archived':
        return styles.statusArchived || 'badge bg-dark';
      default:
        return styles.statusAssigned;
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'active':
        return 'Active';
      case 'assigned':
        return 'Assigned';
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'overdue':
        return 'Overdue';
      case 'paused':
        return 'Paused';
      case 'archived':
        return 'Archived';
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
    }
  };

  return (
    <div className={styles.viewerContainer}>
      <div className={styles.viewerHeader}>
        <h3 className={styles.viewerTitle}>Student View: {mission.title}</h3>
        <div className={styles.viewerControls}>
          <button 
            className={`btn btn-sm ${viewMode === 'details' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('details')}
          >
            Mission Details
          </button>
          <button 
            className={`btn btn-sm ${viewMode === 'workspace' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('workspace')}
          >
            Mission Workspace
          </button>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <div className={styles.viewerContent}>
        {viewMode === 'details' ? (
          <div className={styles.detailsView}>
            <div className={styles.missionSection}>
              <h4 className={styles.sectionTitle}>Mission Description</h4>
              <div className={styles.missionDescription}>
                <SimpleMarkdown>{mission.description}</SimpleMarkdown>
              </div>
            </div>

            <div className={styles.missionSection}>
              <h4 className={styles.sectionTitle}>Mission Information</h4>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <strong>Status:</strong>
                  <span className={`${styles.statusBadge} ${getStatusBadgeClass(mission.status)}`}>
                    {getStatusDisplayName(mission.status)}
                  </span>
                </div>
                
                <div className={styles.infoItem}>
                  <strong>Difficulty:</strong>
                  <span>{mission.difficulty || 'Medium'}</span>
                </div>
                
                {mission.dueDate && (
                  <div className={styles.infoItem}>
                    <strong>Due Date:</strong>
                    <span>{new Date(mission.dueDate).toLocaleDateString()}</span>
                  </div>
                )}
                
                {typeof mission.progress === 'number' && mission.progress > 0 && (
                  <div className={styles.infoItem}>
                    <strong>Progress:</strong>
                    <span>{mission.progress}%</span>
                  </div>
                )}
                
                {mission.score > 0 && (
                  <div className={styles.infoItem}>
                    <strong>Score:</strong>
                    <span>{mission.score} points</span>
                  </div>
                )}
              </div>
            </div>

            {mission.resourceTypes && mission.resourceTypes.length > 0 && (
              <div className={styles.missionSection}>
                <h4 className={styles.sectionTitle}>Available Resources</h4>
                <div className={styles.resourceTypes}>
                  {mission.resourceTypes.includes('video') && (
                    <div className={styles.resourceType}>
                      <span className={styles.resourceIcon}>📹</span>
                      <span>Video Resources</span>
                    </div>
                  )}
                  {mission.resourceTypes.includes('document') && (
                    <div className={styles.resourceType}>
                      <span className={styles.resourceIcon}>📄</span>
                      <span>Documents</span>
                    </div>
                  )}
                  {mission.resourceTypes.includes('interactive') && (
                    <div className={styles.resourceType}>
                      <span className={styles.resourceIcon}>🎮</span>
                      <span>Interactive Activities</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.workspaceView}>
            <div className={styles.workspaceSection}>
              <h4 className={styles.sectionTitle}>Mission Description</h4>
              <div className={styles.workspaceDescription}>
                <SimpleMarkdown>{mission.description}</SimpleMarkdown>
              </div>
            </div>

            {/* Rich Content Section */}
            {mission.resourceTypes?.includes('video') && (
              <div className={styles.contentCard}>
                <h5 className={styles.contentCardTitle}>📹 Video Resources</h5>
                <div className={styles.videoPlaceholder}>
                  <p>Video content will be displayed here</p>
                  <small>Teachers can embed videos in mission descriptions</small>
                </div>
              </div>
            )}
            {mission.resourceTypes?.includes('document') && (
              <div className={styles.contentCard}>
                <h5 className={styles.contentCardTitle}>📄 Documents</h5>
                <div className={styles.documentPlaceholder}>
                  <p>PDF and document links will appear here</p>
                  <small>Teachers can attach reading materials</small>
                </div>
              </div>
            )}
            {mission.resourceTypes?.includes('interactive') && (
              <div className={styles.contentCard}>
                <h5 className={styles.contentCardTitle}>🎮 Interactive Activities</h5>
                <div className={styles.interactivePlaceholder}>
                  <p>Quizzes, puzzles, and interactive content</p>
                  <small>Teachers can add interactive elements</small>
                </div>
              </div>
            )}

            <div className={styles.workspaceSection}>
              <h4 className={styles.sectionTitle}>Student Tasks</h4>
              <div className={styles.taskList}>
                {[
                  'Read through the mission description carefully',
                  'Complete the required activities',
                  'Take notes on important points',
                  'Review your work before submitting'
                ].map((task, index) => (
                  <div key={index} className={styles.taskItem}>
                    <input
                      type="checkbox"
                      id={`task-${index}`}
                      disabled
                      className={styles.taskCheckbox}
                    />
                    <label htmlFor={`task-${index}`} className={styles.taskLabel}>
                      {task}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.workspaceSection}>
              <h4 className={styles.sectionTitle}>Student Notes Area</h4>
              <textarea
                className={styles.notesTextarea}
                placeholder="Students can add their notes, thoughts, or answers here..."
                disabled
                rows={6}
              />
            </div>

            <div className={styles.workspaceFooter}>
              <div className={styles.workspaceInfo}>
                <p><strong>Note:</strong> This is how students see the mission workspace.</p>
                <p>Students can check off tasks, take notes, save progress, and submit their work.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherMissionViewer; 